import { Box, useDisclosure } from "@chakra-ui/react"
import { EntityId, FulfillmentTypes } from "@jackfruit/common"
import React, { MouseEvent, useCallback, useEffect, useState } from "react"
import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import FulfillmentUpdateConfirmationDialog from "~/components/blocks/FulfillmentUpdateConfirmationDialog"
import { UPLOAD_ACCEPTED_FILE_TYPES } from "~/data/const"
import { useCart } from "~/hooks/useCart"
import { useCurrentPageId } from "~/hooks/useCurrentPageId"
import { usePageSession } from "~/hooks/usePageSession"
import { actions } from "~/redux/process"
import { parseArgsFromEvent, registerGlobalFunction } from "../register"

/*
    Usage example:

    <button 
    data-product-ids="1234,2304" // List of product IDs
    data-product-category-ids="7,5" // List of category IDs
    data-default-fulfillment="delivery"
    onclick="api.uploadImages(event)"
    >
        Get Started
    </button>
*/

interface FuncArgs {
  productIds: number[]
  productCategoryIds: number[]
  defaultFulfillment: FulfillmentTypes
}

interface Props {}

const FuncUploadImages: React.FC<Props> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const pageId = useCurrentPageId()
  const {
    pageSession: { isReady },
  } = usePageSession()
  const {
    cart: { fulfillment: currentFulfillment },
  } = useCart()

  const [funcArgs, setFuncArgs] = useState<FuncArgs>({
    productIds: [],
    productCategoryIds: [],
    defaultFulfillment: "pickup",
  })
  const { isOpen, onOpen: openDialog, onClose: closeDialog } = useDisclosure()
  const [lineItemsToKeep, setLineItemsToKeep] = useState<EntityId[]>([])
  const [lineItemsToRemove, setLineItemsToRemove] = useState<EntityId[]>([])

  const onDialogDecisionConfirm = useCallback(() => {
    dispatch(actions.productCompatibilityDialog({ choice: true }))
  }, [dispatch])

  const onDialogDecisionCancel = useCallback(() => {
    dispatch(actions.productCompatibilityDialog({ choice: false }))
  }, [dispatch])

  const onClose = useCallback(() => {
    closeDialog()
    setLineItemsToKeep([])
    setLineItemsToRemove([])
  }, [closeDialog])

  const onOpen = useCallback(
    (data: { lineItemsToKeep: EntityId[]; lineItemsToRemove: EntityId[] }) => {
      setLineItemsToKeep(data.lineItemsToKeep)
      setLineItemsToRemove(data.lineItemsToRemove)
      openDialog()
    },
    [openDialog]
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (isReady && acceptedFiles.length > 0) {
        const { productIds, productCategoryIds, defaultFulfillment } = funcArgs
        // upload the first image
        const firstImage = acceptedFiles[0]
        dispatch(
          actions.createUploadFromButton({
            pageId,
            file: firstImage,
            productIds,
            productCategoryIds,
            fulfillment: defaultFulfillment,
          })
        )

        const remainingImages = acceptedFiles.slice(1)
        // finish uploading images
        remainingImages.forEach((file: File) =>
          dispatch(
            actions.createUploadFromButton({
              pageId,
              file,
              productIds,
              productCategoryIds,
              fulfillment: defaultFulfillment,
            })
          )
        )
      }
    },
    [dispatch, funcArgs, isReady, pageId]
  )

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    accept: UPLOAD_ACCEPTED_FILE_TYPES,
  })

  const handleImageUpload = useCallback(
    async (choice: FulfillmentTypes) => {
      if (choice !== currentFulfillment) {
        // Switch fulfillment if target doesn't match
        dispatch(
          actions.updateFulfillment({
            pageId,
            onOpen,
            onClose,
            fulfillment: choice,
            callback: open,
          })
        )
      } else {
        open()
      }
    },
    [currentFulfillment, dispatch, onClose, onOpen, open, pageId]
  )

  const uploadImages = useCallback(
    (event: MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const { productIds, productCategoryIds, defaultFulfillment } =
        parseArgsFromEvent<FuncArgs>(event)

      if (isReady) {
        setFuncArgs({ productIds, productCategoryIds, defaultFulfillment })
        handleImageUpload(defaultFulfillment)
      }
    },
    [isReady, handleImageUpload]
  )

  useEffect(() => {
    if (window) {
      registerGlobalFunction("uploadImages", uploadImages)
    }
  }, [uploadImages])

  if (!isReady) {
    return null
  }

  return (
    <Box
      {...getRootProps({ className: "dropzone" })}
      aria-label="dropzone upload"
    >
      <input {...getInputProps()} />
      <FulfillmentUpdateConfirmationDialog
        title={t(
          "components.api.functions.FunctionAddProductToCart.UpdateConfirmationDialog.title"
        )}
        description={t(
          "components.api.functions.FunctionAddProductToCart.UpdateConfirmationDialog.description"
        )}
        lineItemsToKeep={lineItemsToKeep}
        lineItemsToRemove={lineItemsToRemove}
        onClose={onDialogDecisionCancel}
        onConfirm={onDialogDecisionConfirm}
        isOpen={isOpen}
      />
    </Box>
  )
}

export default FuncUploadImages
